import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";
import { getServerSession } from "next-auth";
import { ApiClient } from "@/lib/api-client";
import { authOptions } from "@/lib/auth";
import { IDropdown } from "@/types/common-type";
import { ServerActionError } from "./action-utils";
import { ERoundingMode } from "@/features/policies/salary-scales";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isClient = () => typeof window !== "undefined";

export function formatDate(date?: string | Date | null): string {
  if (!date) return "";

  const resolvedDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(resolvedDate.getTime())) {
    return "";
  }

  return format(resolvedDate, "dd/MM/yyyy");
}

export function formatDateTime(date?: Date): string {
  if (date) return format(date, "dd/MM/yyyy HH:mm:ss");
  return "";
}

export function getErrorMessage(
  error: unknown,
  fallback: string = "Đã có lỗi xảy ra"
): string {
  if (typeof error === "string") return error;
  if (!error || typeof error !== "object") return fallback;

  const candidate = error as {
    message?: unknown;
    title?: unknown;
    error?: unknown;
    errors?: unknown;
    Errors?: unknown;
  };

  if (typeof candidate.message === "string") return candidate.message;
  if (typeof candidate.title === "string") return candidate.title;
  if (typeof candidate.error === "string") return candidate.error;

  const validationErrors = candidate.errors ?? candidate.Errors;
  if (validationErrors && typeof validationErrors === "object") {
    const firstError = Object.values(
      validationErrors as Record<string, unknown>
    )
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .find((value) => typeof value === "string");

    if (typeof firstError === "string") return firstError;
  }

  return fallback;
}

export function getPagingParams({
  page,
  pageSize,
  sort,
}: {
  page?: number;
  pageSize?: number;
  sort?: string;
}) {
  let orderBy = "";

  if (sort) {
    orderBy = sort
      .split(",")
      .map((s) => {
        const [field, dir] = s.split(".");
        if (!field || !dir) return "";
        return `${field.replace(/_/g, ".")} ${dir}`;
      })
      .filter(Boolean)
      .join(",");
  }

  return {
    page: Number.isFinite(page) ? page!.toString() : "1",
    pageSize: Number.isFinite(pageSize) ? pageSize!.toString() : "20",
    orderBy,
  };
}

export function roundNumber(number: number, digits: number = 2) {
  var multiple = Math.pow(10, digits);
  var result = Math.round(number * multiple) / multiple;
  return result || 0;
}

export function getPathnameWithoutParams(
  pathname: string,
  numParamsToRemove: number = 1
): string {
  const parts = pathname.split("/");
  for (let i = 0; i < numParamsToRemove; i++) {
    parts.pop();
  }
  return parts.join("/");
}

export function getPreviousPath(pathname: string): string {
  const pathItems = pathname.split("/").filter((x) => x);
  const lastNonNumericIndex = pathItems.reduce(
    (acc, item, index) => (isNaN(Number(item)) ? index : acc),
    0
  );
  const currentPathItems = pathItems.slice(0, lastNonNumericIndex + 1);
  const previousPathItems = currentPathItems.slice(0, -1);
  const previousPath = "/" + previousPathItems.join("/");
  return previousPath;
}

export async function createAuthApiClient(
  apiEndpoint: string = String(process.env.INTERNAL_API_ENDPOINT)
): Promise<ApiClient> {
  const session = await getServerSession(authOptions);

  if (!session?.accessToken) {
    throw new ServerActionError("Missing access token");
  }

  const apiClient = new ApiClient(apiEndpoint, session.accessToken);
  return apiClient;
}

export async function createApiClient(
  accessToken: string,
  apiEndpoint: string = String(process.env.INTERNAL_API_ENDPOINT)
): Promise<ApiClient> {
  const apiClient = new ApiClient(apiEndpoint, accessToken);
  return apiClient;
}

export function checkIsNumericAllowUndefined(value: string) {
  const isValidNumber =
    value === undefined || value === null || !isNaN(Number(value));
  return isValidNumber;
}

export function formatCurrency(
  number: number | undefined,
  {
    maxDigits = 2,
    isAllowReturnEmpty = false,
  }: { maxDigits?: number; isAllowReturnEmpty?: boolean } = {}
) {
  if (!number && number !== 0) {
    return isAllowReturnEmpty ? "" : 0;
  }
  const roundedNumber = roundNumber(number, Number(maxDigits));
  return roundedNumber.toLocaleString("de-DE", {
    maximumFractionDigits: maxDigits,
  });
}

export const formatCurrencyAllowUndefined = (value: number | undefined) => {
  return formatCurrency(value, {
    isAllowReturnEmpty: true,
  });
};

export const convertEnumToDropdownItems = (enumObj: any): IDropdown[] => {
  const filteredEntries = Object.entries(enumObj).filter(([key]) =>
    isNaN(Number(key))
  );
  return filteredEntries.map(([name, code]) => ({
    id: code as number,
    name,
    code: code as string,
  }));
};

export function getKeyFromValue(
  enumObj: any,
  value?: number
): string | undefined {
  if (value) {
    for (const key in enumObj) {
      if (enumObj[key] === value) {
        return key;
      }
    }
  }
  return undefined;
}

export function getFirstDayOfCurrentYear(): Date {
  const currentYear = new Date().getFullYear();
  const firstDayOfYear = new Date(currentYear, 0, 1);
  return firstDayOfYear;
}

const PAYROLL_PERIOD_START_DAY = 16;

export function getStartOfCurrentPayrollPeriod(): Date {
  const today = new Date();
  return today.getDate() >= PAYROLL_PERIOD_START_DAY
    ? new Date(today.getFullYear(), today.getMonth(), PAYROLL_PERIOD_START_DAY)
    : new Date(today.getFullYear(), today.getMonth() - 1, PAYROLL_PERIOD_START_DAY);
}

export function formatMonthYear(month?: number, year?: number): string {
  if (month === undefined || year === undefined) {
    return "";
  }

  const formattedMonth = month.toString().padStart(2, "0");
  return `${formattedMonth}/${year}`;
}

const camelToTitleCase = (camelStr: string): string => {
  return camelStr.charAt(0).toUpperCase() + camelStr.slice(1);
};

export const convertKeysToUppercase = <T extends Record<string, any>>(
  obj: T
): Record<string, any> => {
  const newObj: Record<string, any> = {};
  Object.keys(obj).forEach((key) => {
    newObj[camelToTitleCase(key)] = obj[key];
  });
  return newObj;
};

export function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function readMoneyToText(number: number): string {
  const units = ["", "nghìn", "triệu", "tỷ", "nghìn tỷ", "triệu tỷ"];
  const ones = [
    "",
    "một",
    "hai",
    "ba",
    "bốn",
    "năm",
    "sáu",
    "bảy",
    "tám",
    "chín",
  ];
  const tens = [
    "",
    "mười",
    "hai mươi",
    "ba mươi",
    "bốn mươi",
    "năm mươi",
    "sáu mươi",
    "bảy mươi",
    "tám mươi",
    "chín mươi",
  ];
  const hundreds = [
    "",
    "một trăm",
    "hai trăm",
    "ba trăm",
    "bốn trăm",
    "năm trăm",
    "sáu trăm",
    "bảy trăm",
    "tám trăm",
    "chín trăm",
  ];

  if (number === 0) return "không đồng";

  const numStr = Math.abs(number).toString();
  const groups = [];
  for (let i = numStr.length; i > 0; i -= 3) {
    groups.push(numStr.slice(Math.max(0, i - 3), i));
  }
  groups.reverse();

  let result = "";
  for (let i = 0; i < groups.length; i++) {
    const group = parseInt(groups[i]);
    if (group === 0) continue;

    const unitIndex = groups.length - 1 - i;
    const groupText = readThreeDigits(group);

    result += (result ? ", " : "") + groupText + " " + units[unitIndex];
  }

  result = result.replace(/một mươi/g, "mười");
  result = result.replace(/mươi một/g, "mươi mốt");
  result = result.replace(/mươi năm/g, "mươi lăm");
  result = result.replace(/một trăm/g, "trăm");

  return (number < 0 ? "âm " : "") + result + " đồng";

  function readThreeDigits(num: number): string {
    const hundred = Math.floor(num / 100);
    const ten = Math.floor((num % 100) / 10);
    const one = num % 10;

    let text = "";
    if (hundred > 0) {
      text += hundreds[hundred] + " ";
    }

    if (ten > 0) {
      text += tens[ten] + " ";
    }

    if (one > 0) {
      if (ten > 1 && one === 1) {
        text += "mốt";
      } else if (ten > 0 && one === 5) {
        text += "lăm";
      } else {
        text += ones[one];
      }
    } else if (ten === 1) {
      text += "mười";
    }

    return text.trim();
  }
}

export function getBaseResourceUrl(pathname: string): string {
  const localePattern = /^\/[a-z]{2}(\/|$)/;
  const result = pathname.replace(localePattern, "/");

  return result;
}

export function removeTones(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D");
}

export function toAPIDate(date?: Date | null) {
  return date ? format(date, "yyyy-MM-dd") : null;
}

export function roundAmount(
  amount: number,
  step: number,
  mode: ERoundingMode
): number {
  const a = Number(amount);
  const s = Number(step);

  if (!Number.isFinite(a) || !Number.isFinite(s) || s <= 0) return a;

  const q = a / s;

  if (mode === ERoundingMode.Up) return Math.ceil(q) * s;
  if (mode === ERoundingMode.Down) return Math.floor(q) * s;
  if (mode === ERoundingMode.Nearest) return Math.round(q) * s;

  return a;
}
